/**
 * Add Observation State
 *
 * NGXS state for managing observation data during the add/create process.
 * This state is stored in local storage to preserve form data across navigation.
 */

import { Injectable } from '@angular/core';
import {
  State,
  Action,
  StateContext,
  Selector,
  Store,
  NgxsOnInit,
} from '@ngxs/store';
import {
  InitializeAddObservation,
  SetObservationField,
  UpdateObservationFields,
  ResetAddObservation,
  SetObservationData,
  ClearAddObservation,
} from './add-observation.actions';
import { Observation } from 'src/app/models/schema';
import { AuthState } from 'src/app/store/auth';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * Add Observation State Model
 *
 * Extends Observation model with all the same fields for form state management.
 */
export type AddObservationStateModel = Partial<Observation>;

/**
 * Default state
 */
/**
 * Default state values
 */
const defaults: AddObservationStateModel = {
  id: '',
  studentId: '',
  skillId: '',
  setting: '',
  observationDuration: undefined,
  notes: '',
  performedIndependently: undefined,
  promptingVerbal: 0,
  promptingVisual: 0,
  promptingGestural: 0,
  promptingModel: 0,
  promptingPartialPhysical: 0,
  promptingFullPhysical: 0,
  promptingComments: '',
  accommodationsProvided: undefined,
  accommodations: [],
  accommodationComments: '',
  modificationsMade: undefined,
  modifications: [],
  modificationComments: '',
  accuracyNumerator: undefined,
  accuracyDenominator: undefined,
  accuracyPercentage: undefined,
  duration: undefined,
  frequencyCount: undefined,
  frequencyTimeUnit: '',
  latency: undefined,
  magnitude: undefined,
  independencePercentage: undefined,
  createdAt: undefined,
  createdBy: undefined,
  updatedAt: undefined,
  updatedBy: undefined,
};

/**
 * Add Observation State
 * Stored in local storage with key 'addObservation'
 */
@State<AddObservationStateModel>({
  name: 'addObservation',
  defaults,
})
@Injectable()
export class AddObservationState implements NgxsOnInit {
  constructor(private store: Store) {}

  /**
   * Initialize state listeners
   *
   * Listens for auth state changes and clears state if user is not authenticated.
   * This handles all cases where user becomes unauthenticated (sign out, account deletion, etc.)
   */
  ngxsOnInit(ctx: StateContext<AddObservationStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes
    this.store
      .select(AuthState.initialized)
      .pipe(
        distinctUntilChanged() // Only emit when authentication status actually changes
      )
      .subscribe((isAuthenticated) => {
        // Only clear if we're transitioning from authenticated to unauthenticated
        // (not on initial load where auth state might not be hydrated yet)
        if (previousAuthState === true && !isAuthenticated) {
          // Reset state
          ctx.setState(defaults);

          // Clear the locally stored addObservation from localStorage
          try {
            localStorage.removeItem('@@STATE.addObservation');
            console.log(
              '[AddObservationState] Cleared addObservation from localStorage'
            );
          } catch (error) {
            console.warn(
              '[AddObservationState] Failed to clear addObservation from localStorage:',
              error
            );
          }

          console.log(
            '[AddObservationState] State cleared - user signed out'
          );
        }

        // Track previous auth state
        previousAuthState = isAuthenticated;
      });
  }

  /**
   * Get the entire add observation state
   */
  @Selector()
  static getState(state: AddObservationStateModel): AddObservationStateModel {
    return state;
  }

  /**
   * Get student ID
   */
  @Selector()
  static getStudentId(state: AddObservationStateModel): string | undefined {
    return state.studentId;
  }

  /**
   * Get skill ID
   */
  @Selector()
  static getSkillId(state: AddObservationStateModel): string | undefined {
    return state.skillId;
  }

  /**
   * Get setting
   */
  @Selector()
  static getSetting(state: AddObservationStateModel): string | undefined {
    return state.setting;
  }

  /**
   * Get observation duration
   */
  @Selector()
  static getObservationDuration(
    state: AddObservationStateModel
  ): number | undefined {
    return state.observationDuration;
  }

  /**
   * Get notes
   */
  @Selector()
  static getNotes(state: AddObservationStateModel): string | undefined {
    return state.notes;
  }

  /**
   * Get observation data as Observation object
   */
  @Selector()
  static getObservationData(state: AddObservationStateModel): Partial<Observation> {
    return state; // Since AddObservationStateModel is now Partial<Observation>, just return it
  }

  /**
   * Check if form has any meaningful data (beyond just IDs)
   */
  @Selector()
  static hasData(state: AddObservationStateModel): boolean {
    return !!(
      state.setting ||
      state.observationDuration ||
      state.notes ||
      state.performedIndependently !== undefined ||
      state.promptingVerbal ||
      state.promptingVisual ||
      state.promptingGestural ||
      state.promptingModel ||
      state.promptingPartialPhysical ||
      state.promptingFullPhysical ||
      state.promptingComments ||
      (state.accommodations && state.accommodations.length > 0) ||
      state.accommodationComments ||
      (state.modifications && state.modifications.length > 0) ||
      state.modificationComments ||
      state.accuracyNumerator !== undefined ||
      state.accuracyDenominator !== undefined ||
      state.duration ||
      state.frequencyCount ||
      state.frequencyTimeUnit ||
      state.latency ||
      state.magnitude
    );
  }

  /**
   * Initialize add observation form
   * Only resets to defaults if there's no existing data
   */
  @Action(InitializeAddObservation)
  initializeAddObservation(ctx: StateContext<AddObservationStateModel>) {
    const currentState = ctx.getState();

    // Check if there's any meaningful data (beyond empty defaults)
    const hasData = !!(
      (currentState.studentId && currentState.studentId !== '') ||
      (currentState.skillId && currentState.skillId !== '') ||
      (currentState.setting && currentState.setting !== '') ||
      currentState.observationDuration ||
      (currentState.notes && currentState.notes !== '') ||
      currentState.promptingVerbal ||
      currentState.promptingVisual ||
      currentState.promptingGestural ||
      currentState.promptingModel ||
      currentState.promptingPartialPhysical ||
      currentState.promptingFullPhysical ||
      (currentState.promptingComments && currentState.promptingComments !== '') ||
      (currentState.accommodations && currentState.accommodations.length > 0) ||
      (currentState.accommodationComments && currentState.accommodationComments !== '') ||
      (currentState.modifications && currentState.modifications.length > 0) ||
      (currentState.modificationComments && currentState.modificationComments !== '') ||
      currentState.accuracyNumerator !== null ||
      currentState.accuracyDenominator !== null ||
      currentState.duration !== null ||
      currentState.frequencyCount !== null ||
      (currentState.frequencyTimeUnit && currentState.frequencyTimeUnit !== '') ||
      currentState.latency !== null ||
      currentState.magnitude !== null
    );

    if (!hasData) {
      ctx.setState(defaults);
    }
  }

  /**
   * Set an observation field
   */
  @Action(SetObservationField)
  setObservationField(
    ctx: StateContext<AddObservationStateModel>,
    action: SetObservationField
  ) {
    ctx.patchState({
      [action.field]: action.value,
    } as Partial<AddObservationStateModel>);
  }

  /**
   * Update multiple observation fields
   */
  @Action(UpdateObservationFields)
  updateObservationFields(
    ctx: StateContext<AddObservationStateModel>,
    action: UpdateObservationFields
  ) {
    ctx.patchState(action.updates as Partial<AddObservationStateModel>);
  }

  /**
   * Reset add observation form
   */
  @Action(ResetAddObservation)
  resetAddObservation(ctx: StateContext<AddObservationStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Set observation data (e.g., when editing)
   */
  @Action(SetObservationData)
  setObservationData(
    ctx: StateContext<AddObservationStateModel>,
    action: SetObservationData
  ) {
    ctx.patchState(action.observation as Partial<AddObservationStateModel>);
  }

  /**
   * Clear add observation form
   */
  @Action(ClearAddObservation)
  clearAddObservation(ctx: StateContext<AddObservationStateModel>) {
    ctx.setState(defaults);
  }
}
